import axios from 'axios';
import BigNumber from 'bignumber.js';
import { ContractAddress } from './constants/addresses';
import { ONCHAIN_SERVER_BASE_URL } from './constants/apollo';
import { NetworkChainIds } from '@dapp/multi-lib/network';

/**
 *
 * @param {String | Number} amount Amount to be adjusted for the slippage
 * @param {Number} slippage Slippage value in percentage
 * @returns {String}
 */
export const addSlippage = (amount: string | number, slippage: number) => {
  const mulFactor = 1 + slippage / 100;
  return BigNumber(amount.toString())
    .multipliedBy(mulFactor)
    .decimalPlaces(8)
    .toString();
};

/**
 *
 * @param {BigNumber | String | Number} amount Amount to be adjusted for the slippage
 * @param {Number} slippage Slippage value in percentage
 * @returns {String}
 */
export const reduceSlippage = (amount: string | number, slippage: number) => {
  const mulFactor = 1 - slippage / 100;
  return BigNumber(amount.toString())
    .multipliedBy(mulFactor)
    .decimalPlaces(8)
    .toString();
};

/**
 *
 * @param {Contract} contract ethersJs contract instance
 * @param {String} methodName Name of the contract function to be executed
 * @param {[any]} args Arguments for the contract function
 * @returns {Promise<ContractTransaction>} Transaction object
 */
export const executeContractMethod = async (
  contract: Record<string, (...a: unknown[]) => unknown> & {
    estimateGas: Record<string, (...a: unknown[]) => Promise<unknown>>;
  },
  methodName: string,
  gasPrice: unknown,
  ...args: unknown[]
) => {
  const gasLimit = await contract.estimateGas[methodName](...args);
  return contract[methodName](...args, { gasLimit, gasPrice });
};

/**
 * @returns {number} Deadline for 20 hours from now
 */
export const getDeadline = () => Math.floor(Date.now() / 1000) + 60 * 20;

// 20 minutes to submit after signing
const DEADLINE_BUFFER = 20 * 60;

export const getCurrentBlock = async (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  library: any
): Promise<{ number: number; timestamp: number }> => {
  const lib = library.eth ? library.eth : library;
  const currentBlock = await lib.getBlockNumber();

  const block = await lib.getBlock(currentBlock);

  return {
    number: block.number,
    timestamp: block.timestamp * 1000,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const calculateDeadline = async (library: any) => {
  const block = await getCurrentBlock(library);
  return block.timestamp + DEADLINE_BUFFER;
};

export const defaultGasLimit = 210000;
const MaxUint256 = BigNumber(
  '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
);
export const defaultApprovalAmount = MaxUint256.toFixed(0);
export const unitBlockTime = 15000; // milliseconds
export const BLOCKS_PER_DAY = (60 * 60 * 24 * 1000) / unitBlockTime;

const APY_SECONDS_INTERVAL_REFRESH = 120;
export const APY_STORAGE_KEYS = {
  LAST_REFRESH: 'LAST_REFRESH',
  FARMS_APY: 'FARMS_APY',
  POOL_INFO: 'POOL_INFO',
  UNBONDED_APY: 'UNBONDED_STAKING',
  UNBONDED_APY_BSC: 'UNBONDED_STAKING_BSC',
  BONDED_APY: 'BONDED_STAKING',
  BONDED_APY_NTX: 'BONDED_STAKING_NTX',
  UNBONDED_APY_NTX: 'UNBONDED_STAKING_NTX',
  UNBONDED_APY_SKYA: 'UNBONDED_STAKING_SKYA',
  UNBONDED_APY_NTX_BSC: 'UNBONDED_STAKING_NTX_BSC',
  UNBONDED_APY_RJV: 'UNBONDED_STAKING_RJV',
  UNBONDED_APY_RJVOLD: 'UNBONDED_STAKING_RJVOLD',
  UNBONDED_APY_RJV_BSC: 'UNBONDED_STAKING_RJV_BSC',
  UNBONDED_APY_RJVOLD_BSC: 'UNBONDED_STAKING_RJVOLD_BSC',
  UNBONDED_APY_HYPC: 'UNBONDED_STAKING_HYPC',
  UNBONDED_APY_HYPC_BSC: 'UNBONDED_STAKING_HYPC_BSC',
  UNBONDED_APY_CGV: 'UNBONDED_STAKING_CGV',
  UNBONDED_APY_CGV_BSC: 'UNBONDED_STAKING_CGV_BSC',
  UNBONDED_APY_SOPH: 'UNBONDED_STAKING_SOPH',
  UNBONDED_APY_SOPH_BSC: 'UNBONDED_STAKING_SOPH_BSC',
  UNBONDED_APY_SOPH_BASE: 'UNBONDED_STAKING_SOPH_BASE',
  UNBONDED_APY_SKYA_BASE: 'UNBONDED_STAKING_SKYA_BASE',
};

const DEFAULT_APY = '...';

export const fetchPoolInfo = async (
  token1: string,
  token2: string,
  address: string,
  poolChainId: string | number
) => {
  try {
    if (token1 && token2) {
      const cacheKey =
        `${token1.toUpperCase()}-${token2.toUpperCase()}-${address}-${poolChainId}`.replace(
          /-ETH/,
          '-WETH'
        );
      const storageKeyRefresh = `${APY_STORAGE_KEYS.FARMS_APY}_${APY_STORAGE_KEYS.LAST_REFRESH}_${cacheKey}`;
      const storageKey = `${APY_STORAGE_KEYS.FARMS_APY}_${cacheKey}`;
      const lastRefresh = localStorage.getItem(storageKeyRefresh);
      if (lastRefresh) {
        const poolInfoValue = localStorage.getItem(storageKey);
        const timeSinceRefresh = Date.now() - Number(lastRefresh);
        if (
          timeSinceRefresh < APY_SECONDS_INTERVAL_REFRESH * 1000 &&
          poolInfoValue &&
          poolInfoValue !== 'undefined'
        ) {
          const value = JSON.parse(poolInfoValue);
          if (value !== null) {
            return value;
          }
        }
      }
      localStorage.setItem(storageKeyRefresh, String(Date.now()));
      return await axios
        .get(
          ONCHAIN_SERVER_BASE_URL +
            `/apy/farm/${address}` +
            `?token1=${token1}&token2=${token2}&chainId=${poolChainId}`
        )
        .then(function (req) {
          const result = Array.isArray(req.data.result)
            ? req.data.result.find(
                (pool: { poolName: string }) => pool.poolName === cacheKey
              )
            : req.data.result;
          result && localStorage.setItem(storageKey, JSON.stringify(result));
          return result;
        });
    }
    return '';
  } catch (e) {
    console.error('apy error', e);
    return '';
  }
};

const getStakingApyWithCache = async (url: string, storageKey: string) => {
  try {
    if (url) {
      const lastRefresh = localStorage.getItem(
        `${storageKey}_${APY_STORAGE_KEYS.LAST_REFRESH}`
      );
      if (lastRefresh) {
        const timeSinceRefresh = Date.now() - Number(lastRefresh);
        if (timeSinceRefresh < APY_SECONDS_INTERVAL_REFRESH * 1000) {
          const item = JSON.parse(localStorage.getItem(storageKey) || '');
          if (item !== null) {
            return item;
          }
        }
      }
      localStorage.setItem(
        `${storageKey}_${APY_STORAGE_KEYS.LAST_REFRESH}`,
        String(Date.now())
      );
      return await axios.get(url).then(function (req) {
        const item = {
          apy: req.data.result,
          minimum: req.data.minimum,
        };
        localStorage.setItem(storageKey, JSON.stringify(item));
        return item;
      });
    }
    return {
      apy: '',
      minimum: false,
    };
  } catch (error) {
    console.log('### error', error);
    return {
      apy: '',
      minimum: false,
    };
  }
};

export const requestApyStakingBonded = async (
  address: string,
  chainId: number,
  poolId: number
) => {
  try {
    const url =
      ONCHAIN_SERVER_BASE_URL +
      `/apy/stake/${address}?poolId=${poolId}&chainId=${chainId}`;
    const cacheKey = `${address}_${poolId}_${chainId}`;
    const key = `${APY_STORAGE_KEYS.BONDED_APY}_${cacheKey}`;

    const res = await getStakingApyWithCache(url, key);
    if (!res) {
      return {
        apy: DEFAULT_APY,
      };
    }
    return res;
  } catch (error) {
    console.log('### error', error);
    return {
      apy: DEFAULT_APY,
    };
  }
};

async function requestAPY(
  address: string,
  chainId: number,
  poolId: number | string
) {
  const url =
    ONCHAIN_SERVER_BASE_URL +
    `/apy/stake/${address}?poolId=${poolId}&chainId=${chainId}`;
  const cacheKey = `${address}_${poolId}_${chainId}`;

  const key = `${APY_STORAGE_KEYS.UNBONDED_APY}_${cacheKey}`;
  const res = await getStakingApyWithCache(url, key);
  if (!res) {
    return DEFAULT_APY;
  }
  return res.apy;
}

/**
 *
 * @param {BigNumber | number | string} lpSupply
 * @param {BigNumber | number | string} rewardsPerBlock
 * @param {number} poolId
 * @returns
 */
export const calculateAPY = async (
  address: string,
  chainId = 1,
  poolId?: number | string
) => {
  if (
    !(ContractAddress as Record<number, unknown>)[chainId] ||
    typeof poolId === 'undefined'
  ) {
    return '...';
  }
  const apy = requestAPY(address, chainId, poolId);

  return apy || DEFAULT_APY;
};

export const getCurrentNetworkName = (
  network: NetworkChainIds
): NetworkChainIds => {
  return network;
};
